import React from "react";
import {
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Popover,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { AnnexDocument } from "../../../Core/useAnnexActions";
import { PageRange } from "../../../Models/evaluation";
import colors from "../../../Constants/colors";
import { useTranslation } from "react-i18next";
import { DraggablePredefinedSection } from "./DraggablePredefinedSection";
import { PlaceholderCardDoc } from "./PlaceholderCardDoc";
import { CardDoc } from "./CardDoc";
import { DraggablePredefinedAnnexItem } from "./DraggablePredefinedAnnexItem";
import { Document } from "../../../Models/company";
import { ComoSelect } from "../../Common/ComoSelect";
import { EmptyState } from "./EmptyState";
import { PredefinedSectionDocument } from "../../../Models/appendixStructure";

interface PredefinedAnnexSectionProps {
  predefinedSections: PredefinedSectionDocument[];
  predefinedAnnex: Record<number, AnnexDocument[]>;
  onRemoveDocument: (id: number) => void;
  onUpdateDocument: (
    id: number,
    updates: { name?: string; pageRanges?: PageRange[] | null },
  ) => void;
  onReplacePredefinedSection: (
    sectionId: string,
    options?: {
      docId?: number;
      availableDoc?: Document;
      type?: "title" | "empty_section";
    },
  ) => void;
  predefinedSectionIds: number[];
  onSelectPredefinedSection: (sectionId: number | null) => void;
  selectedPredefinedSection: number | null;
  isLoadingPredefinedData: boolean;
}

export function PredefinedAnnexSection({
  predefinedSections,
  predefinedAnnex,
  onRemoveDocument,
  onUpdateDocument,
  onReplacePredefinedSection,
  predefinedSectionIds,
  onSelectPredefinedSection,
  selectedPredefinedSection,
  isLoadingPredefinedData,
}: PredefinedAnnexSectionProps) {
  const { t } = useTranslation("evaluationPage");
  const [addSectionAnchorEl, setAddSectionAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [selectedSectionId, setSelectedSectionId] = React.useState<
    string | null
  >(null);

  const displayedSections = predefinedSections;

  const sectionOptions = predefinedSectionIds.map((id) => ({
    id,
    label: `${t("evaluationPage.section")} ${id}`,
  }));

  const selectedSectionOption = selectedPredefinedSection
    ? sectionOptions.find((o) => o.id === selectedPredefinedSection)
    : undefined;

  const handleAddSectionMenuClose = () => {
    setAddSectionAnchorEl(null);
    setSelectedSectionId(null);
  };

  const handleAddSectionClick = (
    event: React.MouseEvent<HTMLElement>,
    sectionId: string,
  ) => {
    setSelectedSectionId(sectionId);
    setAddSectionAnchorEl(event.currentTarget);
  };

  const isAddSectionMenuOpen = Boolean(addSectionAnchorEl);

  const getDocumentByPlaceholderId = (
    placeholderId?: string,
  ): AnnexDocument | undefined => {
    if (!placeholderId) return undefined;

    if (!selectedPredefinedSection) return undefined;

    if (!predefinedAnnex[selectedPredefinedSection]) return undefined;

    return predefinedAnnex[selectedPredefinedSection].find(
      (doc) => doc.placeholder_id === placeholderId,
    );
  };

  if (isLoadingPredefinedData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <CircularProgress sx={{ color: colors.primary_dark }} />
      </Box>
    );
  }

  if (predefinedSectionIds.length === 0) {
    return <EmptyState text={t("evaluationPage.noPredefinedSections")} />;
  }

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: colors.gray700, fontWeight: 600 }}
        >
          {t("evaluationPage.inAnnex")}
        </Typography>
        {predefinedSectionIds.length > 1 && (
          <ComoSelect
            options={sectionOptions}
            selectedOption={selectedSectionOption}
            onSelect={(option) =>
              onSelectPredefinedSection(option.id as number)
            }
            placeholder={t("evaluationPage.selectPredefinedSection")}
            sx={{
              mb: 0,
              maxWidth: 200,
              "& .MuiButton-root": {
                py: 0.5,
                px: 1.5,
                fontSize: "0.75rem",
                minHeight: "unset",
              },
            }}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {selectedPredefinedSection &&
          displayedSections.map((d) => {
            const matchingDoc = getDocumentByPlaceholderId(d.id);
            return (
              <React.Fragment key={d.id || d.name}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ flex: 1, position: "relative" }}>
                    <DraggablePredefinedSection
                      doc={d}
                      disabled={!!matchingDoc}
                    >
                      <Box
                        sx={{
                          opacity: 0.7,
                        }}
                      >
                        <PlaceholderCardDoc key={d.id || d.name} doc={d} />
                      </Box>{" "}
                    </DraggablePredefinedSection>
                    {matchingDoc && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 1.5,
                          left: 0,
                          right: 0,
                          zIndex: 1,
                        }}
                      >
                        <DraggablePredefinedAnnexItem doc={matchingDoc}>
                          <CardDoc
                            doc={matchingDoc}
                            onCancel={() =>
                              matchingDoc.id && onRemoveDocument(matchingDoc.id)
                            }
                            editable
                            onEditDone={(name) =>
                              matchingDoc.id &&
                              onUpdateDocument(matchingDoc.id, { name })
                            }
                            onPageRangesChange={(ranges) =>
                              matchingDoc.id &&
                              onUpdateDocument(matchingDoc.id, {
                                pageRanges: ranges,
                              })
                            }
                          />
                        </DraggablePredefinedAnnexItem>
                      </Box>
                    )}
                  </Box>
                  <Tooltip title={t("evaluationPage.addToAnnex")}>
                    <IconButton
                      size="small"
                      disabled={!!matchingDoc}
                      onClick={(e) => handleAddSectionClick(e, d.id!)}
                      sx={{
                        color: colors.accent,
                        opacity: matchingDoc ? 0.5 : 1,
                        "&:hover": {
                          bgcolor: colors.gray100,
                        },
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {d.subsections && d.subsections.length > 0 && (
                  <Box
                    sx={{
                      pl: 5,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {d.subsections.map((sub) => {
                      const matchingSubDoc = getDocumentByPlaceholderId(sub.id);
                      return (
                        <Box
                          key={sub.id || sub.name}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box sx={{ flex: 1, position: "relative" }}>
                            <DraggablePredefinedSection
                              doc={sub}
                              disabled={!!matchingSubDoc}
                            >
                              <Box
                                sx={{
                                  opacity: 0.7,
                                }}
                              >
                                <PlaceholderCardDoc
                                  key={sub.id || sub.name}
                                  doc={sub}
                                />
                              </Box>
                            </DraggablePredefinedSection>
                            {matchingSubDoc && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 1.5,
                                  left: 0,
                                  right: 0,
                                  zIndex: 1,
                                }}
                              >
                                <DraggablePredefinedAnnexItem
                                  doc={matchingSubDoc}
                                >
                                  <CardDoc
                                    doc={matchingSubDoc}
                                    onCancel={() =>
                                      matchingSubDoc.id &&
                                      onRemoveDocument(matchingSubDoc.id)
                                    }
                                    editable
                                    onEditDone={(name) =>
                                      matchingSubDoc.id &&
                                      onUpdateDocument(matchingSubDoc.id, {
                                        name,
                                      })
                                    }
                                    onPageRangesChange={(ranges) =>
                                      matchingSubDoc.id &&
                                      onUpdateDocument(matchingSubDoc.id, {
                                        pageRanges: ranges,
                                      })
                                    }
                                  />
                                </DraggablePredefinedAnnexItem>
                              </Box>
                            )}
                          </Box>
                          <Tooltip title={t("evaluationPage.addToAnnex")}>
                            <IconButton
                              size="small"
                              disabled={!!matchingSubDoc}
                              onClick={(e) => handleAddSectionClick(e, sub.id!)}
                              sx={{
                                color: colors.accent,
                                opacity: matchingSubDoc ? 0.5 : 1,
                                "&:hover": {
                                  bgcolor: colors.gray100,
                                },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </React.Fragment>
            );
          })}
      </Box>

      <Popover
        open={isAddSectionMenuOpen}
        anchorEl={addSectionAnchorEl}
        onClose={handleAddSectionMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 2,
            minWidth: 200,
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Box
          onClick={() => {
            if (selectedSectionId) {
              onReplacePredefinedSection(selectedSectionId, {
                type: "empty_section",
              });
            }
            handleAddSectionMenuClose();
          }}
          sx={{
            px: 2,
            py: 1.5,
            cursor: "pointer",
            bgcolor: "white",
            "&:hover": {
              bgcolor: colors.gray50,
            },
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: colors.gray900,
            }}
          >
            {t("evaluationPage.addEmpty")}
          </Typography>
        </Box>
        <Box
          onClick={() => {
            if (selectedSectionId) {
              onReplacePredefinedSection(selectedSectionId, { type: "title" });
            }
            handleAddSectionMenuClose();
          }}
          sx={{
            px: 2,
            py: 1.5,
            cursor: "pointer",
            bgcolor: "white",
            "&:hover": {
              bgcolor: colors.gray50,
            },
            borderBottomLeftRadius: 2,
            borderBottomRightRadius: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: colors.gray900,
            }}
          >
            {t("evaluationPage.addTitle")}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
